import {Attendee} from 'Event/attendee'
import moment from 'moment-timezone'
import {Widget} from 'Event/Widget/template'

export function getUrlAttribute(key: string): string | null {
  const script = document.currentScript as HTMLScriptElement | null
  if (!script) {
    return null
  }

  const url = new URL(script.src, window.location.href)
  return (url.searchParams.get(key) || '').toLowerCase()
}

export function redirectToUrlAfterDelay(url: string, seconds: number) {
  setTimeout(() => {
    if (!url) return
    window.location.href = url
  }, seconds * 1000)
}

export function redirectOnClick(url: string) {
  if (!url) return
  window.location.href = url
}

export function checkOriginAllowed(allowedOrigins: string[] | null): boolean {
  const currentDomain = new URL(window.location.origin).hostname

  if (allowedOrigins === null) {
    return true
  }

  return allowedOrigins.some((origin) => {
    if (origin === null) return true

    const urlWithProtocol =
      origin.startsWith('http://') || origin.startsWith('https://')
        ? origin
        : `https://${origin}`
    const allowedDomain = new URL(urlWithProtocol).hostname

    if (allowedDomain === currentDomain) {
      return true
    }

    const decodedAllowedDomain = decodeURIComponent(allowedDomain)
    if (decodedAllowedDomain.startsWith('*.')) {
      const baseDomain = decodedAllowedDomain.slice(2)

      return (
        currentDomain === baseDomain || currentDomain.endsWith(`.${baseDomain}`)
      )
    }

    return false
  })
}

const getCalendarTitle = (widget: Widget) => {
  return widget.template.calendar.title && widget.template.calendar.title !== ''
    ? widget.template.calendar.title
    : widget.event_title || ''
}
const getCalendarDescription = (widget: Widget) => {
  return widget.template.calendar.description &&
    widget.template.calendar.description !== ''
    ? widget.template.calendar.description
    : widget.event_description || ''
}

export function handleGoogleCalendarClick(
  widget: Widget,
  loginUrl: Attendee['login_url'],
) {
  const format =
    widget.template.calendar.eventType === 'multi'
      ? 'YYYYMMDD'
      : 'YYYYMMDDTHHmmss'

  const title = encodeURIComponent(getCalendarTitle(widget))
  const start = moment(widget.template.calendar.eventStart).format(format)
  const end = moment(widget.template.calendar.eventEnd).format(format)
  const timezone = encodeURIComponent(widget.timezone)
  const location = encodeURIComponent(loginUrl)
  const rawDescription = getCalendarDescription(widget)
  const description = encodeURIComponent(rawDescription.replace(/\n/g, '<br/>'))

  const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&ctz=${timezone}&location=${location}&details=${description}`

  window.location.href = url
}

export function handleOutlookClick(
  widget: Widget,
  loginUrl: Attendee['login_url'],
) {
  const isAllDay = widget.template.calendar.eventType === 'multi'

  const title = encodeURIComponent(getCalendarTitle(widget))
  const location = encodeURIComponent(loginUrl)
  const rawDescription = getCalendarDescription(widget)
  const description = encodeURIComponent(rawDescription.replace(/\n/g, '<br/>'))

  const start = moment
    .tz(widget.template.calendar.eventStart, widget.timezone)
    .format('YYYY-MM-DDTHH:mm:ssZ')

  const end = isAllDay
    ? moment
        .tz(widget.template.calendar.eventEnd, widget.timezone)
        .add(1, 'day')
        .format('YYYY-MM-DDTHH:mm:ssZ')
    : moment
        .tz(widget.template.calendar.eventEnd, widget.timezone)
        .format('YYYY-MM-DDTHH:mm:ssZ')

  const url =
    'https://outlook.live.com/calendar/0/action/compose' +
    `?subject=${title}` +
    `&startdt=${encodeURIComponent(start)}` +
    `&enddt=${encodeURIComponent(end)}` +
    `&location=${location}` +
    `&body=${description}` +
    `&allday=${isAllDay}`
  window.location.href = url
}

export function handleYahooClick(
  widget: Widget,
  loginUrl: Attendee['login_url'],
) {
  const isAllDay = widget.template.calendar.eventType === 'multi'

  const title = encodeURIComponent(getCalendarTitle(widget))
  const location = encodeURIComponent(loginUrl)
  const description = encodeURIComponent(getCalendarDescription(widget))

  const start = isAllDay
    ? moment(widget.template.calendar.eventStart)
        .startOf('day')
        .format('YYYYMMDDTHHmmss')
    : moment
        .tz(widget.template.calendar.eventStart, widget.timezone)
        .utc()
        .format('YYYYMMDDTHHmmss[Z]')

  const end = isAllDay
    ? moment(widget.template.calendar.eventEnd)
        .endOf('day')
        .format('YYYYMMDDTHHmmss')
    : moment
        .tz(widget.template.calendar.eventEnd, widget.timezone)
        .utc()
        .format('YYYYMMDDTHHmmss[Z]')

  const url =
    'https://calendar.yahoo.com/?' +
    'v=60' +
    `&title=${title}` +
    `&st=${encodeURIComponent(start)}` +
    `&et=${encodeURIComponent(end)}` +
    (isAllDay ? '&dur=allday' : '') +
    `&in_loc=${location}` +
    `&desc=${description}`
  window.location.href = url
}

export function handleAppleClick(
  widget: Widget,
  loginUrl: Attendee['login_url'],
) {
  const isAllDay = widget.template.calendar.eventType === 'multi'

  const title = getCalendarTitle(widget)
  const description = getCalendarDescription(widget)
  const location = loginUrl || ''

  const start = isAllDay
    ? moment(widget.template.calendar.eventStart).format('YYYYMMDD')
    : moment
        .tz(widget.template.calendar.eventStart, widget.timezone)
        .utc()
        .format('YYYYMMDDTHHmmss[Z]')

  const end = isAllDay
    ? moment(widget.template.calendar.eventEnd).add(1, 'day').format('YYYYMMDD')
    : moment
        .tz(widget.template.calendar.eventEnd, widget.timezone)
        .utc()
        .format('YYYYMMDDTHHmmss[Z]')

  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//OBVIO//EVENT//EN
BEGIN:VEVENT
DTSTART:${start}
DTEND:${end}
SUMMARY:${escapeICSText(title)}
DESCRIPTION:${escapeICSText(description)}
LOCATION:${escapeICSText(location)}
END:VEVENT
END:VCALENDAR`

  downloadICS(icsContent, title)
}

function downloadICS(icsContent: string, title: string) {
  const blob = new Blob([icsContent], {type: 'text/calendar;charset=utf-8'})
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${title.replace(/\s+/g, '_')}.ics`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function escapeICSText(text: string) {
  return text.replace(/\\|;|,|\n/g, function (match) {
    switch (match) {
      case '\\':
        return '\\\\'
      case ';':
        return '\\;'
      case ',':
        return '\\,'
      case '\n':
        return '\\n'
      default:
        return match
    }
  })
}
